import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, Input, Select, Spin, Modal, Tag, Typography, Alert } from 'antd';
import { toast } from 'react-hot-toast';
import api from "../interceptors/api";
import { useTranslation } from 'react-i18next';
import StandardButton from '../components/StandardButton';
import ProtectedToolWrapper from '../components/ProtectedToolWrapper';

const { TextArea } = Input;
const { Option } = Select;
const { Text } = Typography;

const modelsByProvider = {
  Gemini: ["imagen-4.0-generate-001", "imagen-4.0-ultra-generate-001", "imagen-4.0-fast-generate-001"],
  OpenAI: ["dall-e-3", "dall-e-2"],
  XAI: ["grok-2-image-1212"],
};

const HistoryItem = ({ item, onView, onDelete }) => {
  const [expanded, setExpanded] = useState(false);
  const [imageLoadError, setImageLoadError] = useState({});
  const { t } = useTranslation();

  return (
    <Card
      className="hover:shadow-md transition-shadow cursor-pointer"
      onClick={() => onView(item)}
      size="small"
    >
      <div className="flex justify-between items-start">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <Tag color="blue">{item.provider}</Tag>
            <Tag color="green">{item.model}</Tag>
            <span className="text-xs text-gray-500 whitespace-nowrap">
              {new Date(item.timestamp).toLocaleString()}
            </span>
          </div>

          {/* Prompt section */}
          <div className="mb-2">
            <Text strong className="text-sm">{t('prompt')}:</Text>
            <div className="mt-1">
              <div className="text-sm text-gray-700 whitespace-pre-wrap break-words">
                {item.prompt && item.prompt.length > 150 && !expanded 
                  ? `${item.prompt.substring(0, 150)}...` 
                  : item.prompt}
              </div>
              {item.prompt && item.prompt.length > 150 && (
                <StandardButton
                  type="link"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setExpanded(!expanded);
                  }}
                  className="p-0 h-auto text-xs mt-1"
                >
                  {expanded ? t('show_less') : t('show_more')}
                </StandardButton>
              )}
            </div>
          </div>

          {/* Image preview section */}
          {item.images && item.images.length > 0 && (
            <div>
              <Text strong className="text-sm text-gray-600">{t('images')}: {item.images.length}</Text>
              <div className="mt-1 flex gap-2 overflow-x-auto py-2">
                {item.images.slice(0, 2).map((url, idx) => (
                  <div key={`${item.id}-${idx}`} className="relative">
                    <img 
                      src={url} 
                      alt={`Preview ${idx + 1}`}
                      className="w-20 h-20 object-cover rounded border"
                      onError={(e) => {
                        setImageLoadError(prev => ({ ...prev, [idx]: true }));
                        e.target.onerror = null;
                        if (item.response && typeof item.response === 'string') {
                          try {
                            const response = JSON.parse(item.response);
                            if (response.s3Urls && response.s3Urls[idx]) {
                              e.target.src = response.s3Urls[idx];
                            }
                          } catch (err) {
                            e.target.src = 'https://via.placeholder.com/80x80?text=Image+Error';
                          }
                        } else {
                          e.target.src = 'https://via.placeholder.com/80x80?text=Image+Error';
                        }
                      }}
                      onLoad={() => setImageLoadError(prev => ({ ...prev, [idx]: false }))}
                    />
                    {imageLoadError[idx] && (
                      <div className="absolute inset-0 flex items-center justify-center bg-gray-100 rounded border">
                        <Text className="text-xs text-gray-500">{t('failed_to_load')}</Text>
                      </div>
                    )}
                  </div>
                ))}
                {item.images.length > 2 && (
                  <div className="w-20 h-20 flex items-center justify-center bg-gray-100 rounded border">
                    <Text className="text-gray-500">+{item.images.length - 2}</Text>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        <StandardButton
          type="secondary"
          danger
          onClick={(e) => {
            e.stopPropagation();
            onDelete(item.id, e);
          }}
          size="small"
          className="flex-shrink-0 ml-2"
        >
          {t('delete')}
        </StandardButton>
      </div>
    </Card>
  );
};

const MultiModelImageGenerator = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [provider, setProvider] = useState('OpenAI');
  const [model, setModel] = useState('dall-e-3');
  const [prompt, setPrompt] = useState('A futuristic cityscape at sunset');
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedHistory, setSelectedHistory] = useState(null);
  const [modalTextExpanded, setModalTextExpanded] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const shareId = searchParams.get("share");
    if (shareId) {
      loadSharedData(shareId);
    }
    loadHistory();
  }, []);

  const loadSharedData = (shareId) => {
    api.get(`/multi-model-image-generator/share/${shareId}`)
      .then(res => {
        const data = res.data;
        setProvider(data.provider);
        setModel(data.model);
        setPrompt(data.prompt);
        if (data.images) {
          setImages(data.images);
        }
        toast.success('Shared configuration loaded successfully!');
      })
      .catch(() => toast.error(t('server_error')));
  };

  const loadHistory = async () => {
    try {
      setHistoryLoading(true);
      setError(null);
      
      const userProfileData = JSON.parse(localStorage.getItem('userProfileData') || '{}');

      if (!userProfileData?.id) {
        setError('Please log in to view history');
        setHistory([]);
        setHistoryLoading(false);
        return;
      }

      const res = await api.get('/multi-model-image-generator/history', {
        params: { limit: 50 }
      });

      if (res.data && res.data.history) {
        const formattedHistory = res.data.history.map(item => {
          if (!item.images || item.images.length === 0) {
            if (item.response && typeof item.response === 'string') {
              try {
                const response = JSON.parse(item.response);
                if (response.s3Urls && Array.isArray(response.s3Urls)) {
                  item.images = response.s3Urls;
                } else if (response.images && Array.isArray(response.images)) {
                  item.images = response.images;
                }
              } catch (_) {
              }
            }
          }
          
          return {
            ...item,
            images: Array.isArray(item.images) ? item.images : [],
            timestamp: item.timestamp || item.created_at
          };
        });
        
        setHistory(formattedHistory);
      } else {
        setHistory([]);
      }

    } catch (err) {
      const errorMessage = err.response?.data?.error || err.response?.data?.details || err.message;
      setError(`Failed to load history: ${errorMessage}`);
      
      toast.error(t('failed_to_load_history') + ': ' + errorMessage);
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      toast.error(t('please_enter_prompt'));
      return;
    }
    
    setLoading(true);
    setImages([]);
    setError(null);
    
    try {
      const userProfileData = JSON.parse(localStorage.getItem('userProfileData') || '{}');

      if (!userProfileData?.id) {
        toast.error('Please log in to use this feature');
        setLoading(false);
        return;
      }

      const res = await api.post('/multi-model-image-generator', {
        provider,
        model,
        prompt
      });
      
      if (res.data && (res.data.images || res.data.s3Urls)) {
        const generatedImages = res.data.s3Urls || res.data.images || [];
        if (generatedImages.length === 0) {
          throw new Error('No images were generated');
        }
        setImages(generatedImages);
        
        await loadHistory();
        
        toast.success('Image generated successfully!');
      } else {
        throw new Error('Invalid response from server');
      }
      
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.response?.data?.details || err.message;
      setError(t('image_generation_failed') + ': ' + errorMessage);
      
      toast.error(t('image_generation_failed') + ': ' + errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyUrl = (url) => {
    navigator.clipboard.writeText(url);
    toast.success(t('copied_to_clipboard'));
  };

  const handleCopyPrompt = (promptText) => {
    navigator.clipboard.writeText(promptText);
    toast.success(t('copied_to_clipboard'));
  };

  const handlePreview = (url) => {
    setPreviewImage(url);
    setPreviewVisible(true);
  };

  const handleViewHistory = (item) => {
    setSelectedHistory(item);
    setModalVisible(true);
    setModalTextExpanded(false);
  };

  const handleDeleteHistory = async (id, e) => {
    e.stopPropagation();
    
    try {
      await api.delete(`/multi-model-image-generator/history/${id}`);

      toast.success(t('history_deleted'));
      loadHistory();
    } catch (err) {
      toast.error(t('failed_to_delete_history') + ': ' + (err.response?.data?.error || err.message));
    }
  };

  const handleClearAllHistory = async () => {
    try {
      await api.delete('/multi-model-image-generator/history');

      toast.success(t('all_history_deleted'));
      setHistory([]);
    } catch (err) {
      toast.error(t('failed_to_clear_history') + ': ' + (err.response?.data?.error || err.message));
    }
  };

  const handleDownloadImage = async (url, filename = '') => {
    try {
      if (!filename) {
        const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
        const extension = url.includes('.png') ? 'png' : 
                        url.includes('.jpg') || url.includes('.jpeg') ? 'jpg' : 
                        url.includes('.webp') ? 'webp' : 'png';
        filename = `ai-generated-${timestamp}.${extension}`;
      }
      
      if (url.startsWith('data:image/')) {
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success(t('image_downloaded'));
        return;
      }
      
      const toastId = toast.loading(t('downloading_image'));
      
      try {
        const response = await fetch(url, {
          headers: {
            'Accept': 'image/*',
          }
        });
        
        if (!response.ok) {
          throw new Error(`Failed to fetch image: ${response.status} ${response.statusText}`);
        }
        
        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = filename;
        
        document.body.appendChild(link);
        link.click();
        
        setTimeout(() => {
          document.body.removeChild(link);
          window.URL.revokeObjectURL(blobUrl);
        }, 100);
        
        toast.success(t('image_downloaded_success'), { id: toastId });
        
      } catch (fetchError) {
        toast.error(t('download_failed_open_tab'), { id: toastId });
        setTimeout(() => {
          window.open(url, '_blank');
        }, 500);
      }
      
    } catch (error) {
      toast.error(t('download_failed') + ': ' + error.message);
      window.open(url, '_blank');
    }
  };

  const toggleModalTextExpansion = () => {
    setModalTextExpanded(!modalTextExpanded);
  };

  return (
    <ProtectedToolWrapper showShareBanner={searchParams.has("share")}>
      <div className="max-w-7xl mx-auto p-4">
        {/* Generation Card */}
        <Card className="p-6 rounded-xl shadow-lg space-y-6 mb-6">
          <div className="mb-4 flex gap-2">
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1">{t('provider')}</label>
              <Select 
                value={provider} 
                onChange={val => { 
                  setProvider(val); 
                  setModel(modelsByProvider[val]?.[0] || ''); 
                }} 
                className="w-40"
              >
                {Object.keys(modelsByProvider).map(p => (
                  <Option key={p} value={p}>{p}</Option>
                ))}
              </Select>
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1">{t('model')}</label>
              <Select value={model} onChange={setModel} className="w-60">
                {modelsByProvider[provider]?.map(m => (
                  <Option key={m} value={m}>{m}</Option>
                )) || <Option value="">{t('no_models_available')}</Option>}
              </Select>
            </div>
          </div>
          
          <TextArea
            rows={6}
            value={prompt}
            onChange={e => setPrompt(e.target.value)}
            placeholder={t('enter_image_prompt')}
            className="whitespace-pre-wrap break-words"
          />
          
          <div className="flex justify-center">
            <StandardButton 
              onClick={handleGenerate} 
              disabled={loading} 
              type="secondary" 
              size="large"
              className="w-48"
              loading={loading}
            >
              {t('generate')}
            </StandardButton>
          </div>
          
          {error && (
            <Alert
              message={t('error')}
              description={error}
              type="error"
              showIcon
              closable
              onClose={() => setError(null)}
              className="mt-4"
            />
          )}
          
          {images.length > 0 && (
            <div className="space-y-6 mt-6">
              <h3 className="text-lg font-semibold">{t('generated_images')}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {images.map((url, idx) => (
                  <div key={`generated-${idx}`} className="border rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-shadow">
                    <img 
                      src={url} 
                      alt={`AI generated image ${idx + 1}`} 
                      className="w-full h-64 object-cover cursor-pointer hover:opacity-95 transition-opacity"
                      onClick={() => handlePreview(url)}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = 'https://via.placeholder.com/400x256?text=Image+Failed+to+Load';
                      }}
                    />
                    <div className="p-3 bg-gray-50 flex justify-between gap-2">
                      <StandardButton 
                        size="small" 
                        onClick={() => handlePreview(url)}
                        className="border-blue-500 text-blue-600 hover:bg-blue-50"
                      >
                        {t('preview')}
                      </StandardButton>
                      <StandardButton 
                        size="small" 
                        onClick={() => handleCopyUrl(url)}
                        className="border-green-500 text-green-600 hover:bg-green-50"
                      >
                        {t('copy_url')}
                      </StandardButton>
                      <StandardButton 
                        size="small" 
                        onClick={() => handleDownloadImage(url)}
                        className="border-purple-500 text-purple-600 hover:bg-purple-50"
                      >
                        {t('download')}
                      </StandardButton>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card>

        {/* History Section */}
        <Card className="p-6 rounded-xl shadow-lg">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">{t('history')}</h3>
            <div className="flex gap-2">
              <StandardButton 
                onClick={loadHistory} 
                loading={historyLoading}
                size="small"
                type="secondary"
              >
                {t('refresh')}
              </StandardButton>
              {history.length > 0 && (
                <StandardButton
                  danger
                  size="small"
                  onClick={handleClearAllHistory}
                  disabled={historyLoading}
                  type="secondary"
                >
                  {t('clear_all')}
                </StandardButton>
              )}
            </div>
          </div>
          
          {historyLoading ? (
            <div className="text-center py-8">
              <Spin size="large" />
              <p className="text-gray-500 mt-2">{t('loading_history')}</p>
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <Alert
                message={t('error_loading_history')}
                description={error}
                type="error"
                showIcon
                className="mb-4"
              />
              <StandardButton 
                onClick={loadHistory}
                type="secondary"
              >
                {t('try_again')}
              </StandardButton>
            </div>
          ) : history.length > 0 ? (
            <div className="space-y-3">
              {history.map((item) => (
                <HistoryItem
                  key={item.id}
                  item={item}
                  onView={handleViewHistory}
                  onDelete={handleDeleteHistory}
                />
              ))}
            </div>
          ) : (
            <Card className="text-center py-8">
              <p className="text-gray-500">{t('no_history_yet')}</p>
              <p className="text-sm text-gray-400 mt-2">{t('generate_to_see_history')}</p>
            </Card>
          )}
        </Card>

        {/* Image Preview Modal */}
        <Modal
          open={previewVisible}
          footer={null}
          onCancel={() => setPreviewVisible(false)}
          width="80vw"
          centered
          className="preview-modal"
          destroyOnClose
        >
          <div className="relative">
            <img
              alt="Preview" 
              style={{ width: '100%', maxHeight: '80vh', objectFit: 'contain' }} 
              src={previewImage}
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = 'https://via.placeholder.com/800x600?text=Image+Failed+to+Load';
              }}
            />
            <div className="absolute bottom-4 right-4 flex gap-2">
              <StandardButton 
                onClick={() => handleDownloadImage(previewImage)}
                type="secondary"
                className="mr-2"
              >
                {t('download')}
              </StandardButton>
              <StandardButton 
                onClick={() => setPreviewVisible(false)}
                type="secondary"
              >
                {t('close')}
              </StandardButton>
            </div>
          </div>
        </Modal>

        {/* History Detail Modal */}
        <Modal
          title={t('history_details')}
          open={modalVisible}
          onCancel={() => setModalVisible(false)}
          width={800}
          footer={[
            <StandardButton key="close" onClick={() => setModalVisible(false)} type="secondary">
              {t('close')}
            </StandardButton>,
            selectedHistory?.images?.[0] && (
              <StandardButton
                key="download"
                type="secondary"
                onClick={() => handleDownloadImage(selectedHistory.images[0])}
              >
                {t('download')}
              </StandardButton>
            )
          ]}
          destroyOnClose
        >
          {selectedHistory && (
            <div className="space-y-4">
              <div className="flex gap-4 flex-wrap">
                <div>
                  <strong>{t('provider')}:</strong> <Tag color="blue">{selectedHistory.provider}</Tag>
                </div>
                <div>
                  <strong>{t('model')}:</strong> <Tag color="green">{selectedHistory.model}</Tag>
                </div>
                <div>
                  <strong>{t('date')}:</strong> {new Date(selectedHistory.timestamp).toLocaleString()}
                </div>
              </div>

              <div>
                <div className="flex justify-between items-center mb-1">
                  <strong>{t('prompt')}:</strong>
                  <div className="flex items-center gap-2">
                    <StandardButton
                      type="secondary"
                      size="small"
                      onClick={() => handleCopyPrompt(selectedHistory.prompt)}
                    >
                      {t('copy')}
                    </StandardButton>
                    {selectedHistory.prompt && selectedHistory.prompt.length > 300 && (
                      <StandardButton
                        type="secondary"
                        size="small"
                        onClick={toggleModalTextExpansion}
                      >
                        {modalTextExpanded ? t('show_less') : t('show_more')}
                      </StandardButton>
                    )}
                  </div>
                </div>
                <Card size="small" className="mt-1">
                  <div className={`p-3 ${!modalTextExpanded ? 'max-h-60' : ''} overflow-y-auto`}>
                    <pre className="whitespace-pre-wrap break-words text-sm m-0 font-sans">
                      {selectedHistory.prompt}
                    </pre>
                  </div>
                </Card>
              </div>

              {selectedHistory.images && selectedHistory.images.length > 0 && (
                <div>
                  <strong>{t('generated_images')} ({selectedHistory.images.length}):</strong>
                  <div className="grid grid-cols-2 gap-4 mt-3">
                    {selectedHistory.images.map((url, idx) => (
                      <div key={`detail-${idx}`} className="border rounded overflow-hidden hover:shadow-md transition-shadow">
                        <img 
                          src={url} 
                          alt={`History image ${idx + 1}`}
                          className="w-full h-48 object-cover cursor-pointer hover:opacity-95 transition-opacity"
                          onClick={() => {
                            setModalVisible(false);
                            handlePreview(url);
                          }}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = 'https://via.placeholder.com/300x192?text=Image+Error';
                          }}
                        />
                        <div className="p-2 bg-gray-50 flex justify-between gap-1">
                          <StandardButton 
                            size="small"
                            type="secondary"
                            onClick={() => {
                              setModalVisible(false);
                              setTimeout(() => handlePreview(url), 100);
                            }}
                            className="text-xs"
                          >
                            {t('preview')}
                          </StandardButton>
                          <StandardButton 
                            size="small"
                            type="secondary"
                            onClick={() => handleDownloadImage(url, `ai-generated-${selectedHistory.provider}-${idx + 1}.png`)}
                            className="text-xs"
                          >
                            {t('download')}
                          </StandardButton>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </Modal>
      </div>
    </ProtectedToolWrapper>
  );
};

export default MultiModelImageGenerator;